import * as React from 'react';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import { LoadingButton } from '@mui/lab';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import {
  EmployeeValues,
  GetWaiversData,
  employeeSettingsSchema,
} from '../../../types/waivers';
import useUpdateWaiver from '../../../hooks/waivers/useUpdateWaiver';

import '../../form/form.css';
import OptionsList from '../OptionsList';

interface Props {
  onSuccess?: () => void;
  draft: GetWaiversData | null | undefined;
}

export default function EmployeeForm({ onSuccess, draft }: Props) {
  const [formError, setFormError] = React.useState('');

  const { mutation, isPending } = useUpdateWaiver({ draft });

  const [initialFormState] = React.useState<EmployeeValues>({
    options: (draft?.employeeSettings?.options || []).sort(
      (a, b) => a.order - b.order,
    ),
  });

  const onSubmit = async (values: EmployeeValues) => {
    if (!draft?.businessId || isPending) return;
    try {
      setFormError('');
      const response = await mutation.mutateAsync({
        employeeSettings: {
          options: values.options,
        },
      });

      if (response) {
        toast.success('Service provider section saved successfully');
        onSuccess?.();
      }
    } catch {
      toast.error('Failed to save service provider section');
    }
  };

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialFormState}
        validationSchema={employeeSettingsSchema}
        onSubmit={onSubmit}>
        {({ isSubmitting, isValid, dirty, values, setFieldValue }) => (
          <Form>
            {formError && (
              <Typography variant="body1" color="primary">
                {formError}
              </Typography>
            )}
            <Box width="100%">
              <OptionsList
                options={values.options}
                onChange={newOptions => setFieldValue('options', newOptions)}
                placeholder="Type a question for your artist"
                addText="Add new question"
                disabled={isSubmitting}
                combineAllOptions
                askForType
                allowedTypes={[
                  'CHECKBOX',
                  'TEXT',
                  'MULTILINE_TEXT',
                  'SIGNATURE',
                  'SINGLE_IMAGE',
                  'MULTIPLE_IMAGE',
                ]}
              />
            </Box>

            <LoadingButton
              type="submit"
              disabled={!isValid || isSubmitting || !dirty || isPending}
              variant="contained"
              className="primary"
              loading={isSubmitting}>
              <span>Save</span>
            </LoadingButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
